import React, { ChangeEvent } from 'react';
import { Box, Button, Checkbox, Container, FormControl, FormControlLabel, FormGroup, FormLabel, Radio, RadioGroup, Slider, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileSearch } from '@fortawesome/pro-light-svg-icons';
import { CvSearchCity, CvSearchState, driverLicencesTypes, DriverLicensesType, getDriverLicencesType } from '../interfaces';
import { CvSearchOption, EducationStatus, QualificationStatus } from '../../../../../api-service/clients';
import { Cell } from '../../../../../components/cell';
import { RadioOption } from '../../../../../components/radio-group';
import { Row } from '../../../../../components/row';
import { SingleSelect } from '../../../../../components/single-select';
import { MultiSelect } from '../../cv-jobsearch/unemployment-enrollment/components/multi-select';
import { LabelWithHelpIcon } from './label-with-icon';
import { TextField } from '../../../../../components/text-field';
import { Accordion, AccordionDetails, AccordionSummary } from '../../../../../components/accordion';
import { ColorPalette } from '../../../../../components/config';

interface ICvSearchDrawerProps {
  searchPanel: CvSearchState;
  isSearching: boolean;
  onSearchClick: () => void;
  onResetClick: () => void;

  onNumberOfKmChange: (km: number) => void;
  onOccupationChange: (value: CvSearchOption[]) => void;
  onQualificationChange: (value: CvSearchOption[]) => void;

  onLanguageChange: (value: CvSearchOption[]) => void;
  onDriversLicenseChange: (value: DriverLicensesType[]) => void;
  onCityChange: (value?: CvSearchCity) => void;
  onFreetextChange: (value?: string) => void;

  onCheckboxChange: (name: string, checked: boolean) => void;
  onEducationStatusChange: (newValue: EducationStatus) => void;
  onQualificationStatusChange: (newValue: QualificationStatus) => void;
}

export function CvSearchDrawer(props: ICvSearchDrawerProps) {
  const {
    searchPanel,
    isSearching,
    onNumberOfKmChange,
    onSearchClick,
    onResetClick,
    onOccupationChange,
    onQualificationChange,
    onFreetextChange,
    onLanguageChange,
    onDriversLicenseChange,
    onCityChange,
    onCheckboxChange,
    onEducationStatusChange,
    onQualificationStatusChange,
  } = props;
  const {
    numberOfKm,
    occupations,
    qualifications,
    languages,
    cities,
    selectedOccupations,
    selectedQualifications,
    freetextSearch,
    selectedLanguages,
    selectedDriversLicenses,
    selectedCity,
    showOnlyJobReady,
    showOnlyFlexjobVisitated,
    showOnlyJobcenter,
    educationStatus,
    qualificationStatus,
  } = searchPanel;

  function handleCheckboxChange(event: ChangeEvent<HTMLInputElement>, checked: boolean): void {
    onCheckboxChange(event.target.name, checked);
  }

  function handleFreetextChange(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    onFreetextChange(event.target.value);
  }

  const drawerWidth = 600;

  return (
    <Container sx={{ width: drawerWidth, border: `solid 1px ${ColorPalette.DisabledInputBackground}`, marginRight: 1 }}>
      <Row>
        <Cell size="full">
          <Box display="flex" mt={1} gap={2} alignItems="center">
            <FontAwesomeIcon icon={faFileSearch} size="xl" />
            <Typography flexGrow={1} variant="h6">
              CV søgekriterier
            </Typography>
          </Box>
        </Cell>
      </Row>
      <Row>
        <Cell size="full">
          <Accordion defaultExpanded>
            <AccordionSummary>
              <LabelWithHelpIcon
                label="Struktureret søgning på jobønsker og erfaring"
                helpTexts={[
                  'Søgning i borgers "Jeg søger job som"-jobønsker.',
                  '',
                  "Du kan afgrænse søgningen til kun at indeholde CV'er fra borgere, der har angivet, at de har uddannelse og/eller erfaring indenfor jobønsket.",
                  '',
                  'Fastholder du "Ikke angivet" under erfaring og uddannelse, indgår borgere både med og uden henholdsvis erfaring og uddannelse.',
                ]}
              />
            </AccordionSummary>
            <AccordionDetails>
              <Row>
                <Cell size="full">
                  <MultiSelect<CvSearchOption>
                    getOptionLabel={x => x.optionName || ''}
                    label="Stillingsbetegnelse"
                    onChange={onOccupationChange}
                    options={occupations}
                    value={selectedOccupations}
                    variant="outlined"
                    renderOption={(prop: object, option: CvSearchOption) => (
                      <li {...prop} key={option.optionId}>
                        {option.optionName}
                      </li>
                    )}
                  />
                </Cell>
              </Row>
              <Row>
                <Cell size="half">
                  <FormControl component="fieldset">
                    <FormLabel id="active-radios" component="legend">
                      Erfaring indenfor jobønske
                    </FormLabel>
                    <RadioGroup color="primary" value={qualificationStatus} onChange={(_, value) => onQualificationStatusChange(value as QualificationStatus)}>
                      <RadioOption control={<Radio size="small" />} value={QualificationStatus.NotSpecified} label="Ikke angivet" />
                      <RadioOption control={<Radio size="small" />} value={QualificationStatus.Without} label="Uden erfaring" />
                      <RadioOption control={<Radio size="small" />} value={QualificationStatus.With} label="Med erfaring" />
                      <RadioOption control={<Radio size="small" />} value={QualificationStatus.With2Years} label="Med erfaring (indenfor 2 år)" />
                      <RadioOption control={<Radio size="small" />} value={QualificationStatus.With5Years} label="Med erfaring (indenfor 5 år)" />
                    </RadioGroup>
                  </FormControl>
                </Cell>
                <Cell size="half">
                  <FormControl component="fieldset">
                    <FormLabel id="active-radios" component="legend">
                      Uddannelse indenfor jobønske
                    </FormLabel>
                    <RadioGroup color="primary" value={educationStatus} onChange={(_, value) => onEducationStatusChange(value as EducationStatus)}>
                      <RadioOption control={<Radio size="small" />} value={EducationStatus.NotSpecified} label="Ikke angivet" />
                      <RadioOption control={<Radio size="small" />} value={EducationStatus.Without} label="Uden uddannelse" />
                      <RadioOption control={<Radio size="small" />} value={EducationStatus.With} label="Med uddannelse" />
                    </RadioGroup>
                  </FormControl>
                </Cell>
              </Row>
            </AccordionDetails>
          </Accordion>
        </Cell>
        <Cell size="full">
          <Accordion>
            <AccordionSummary>
              <LabelWithHelpIcon
                label="Øvrige søgekriterier"
                helpTexts={[
                  'Søgning på fritekst i borgers CV.',
                  // eslint-disable-next-line max-len
                  'Der søges i jobønsker, ansættelsesvilkår, arbejdserfaring, uddannelse, kurser, certificering, kørekort, kvalifikationer, sprog, frivilligt arbejde og Introduktion - Lidt om mig.',
                  '',
                  'Søgning på kvalifikationer, sprog og kørekort i borgers CV.',
                  'Her søges på baggrund af fastdefinerede lister, som borger også kan vælge fra på Jobnet.',
                ]}
              />
            </AccordionSummary>
            <AccordionDetails>
              <Row>
                <Cell size="full">
                  <TextField fullWidth label="Søgeord (fritekst)" variant="outlined" value={freetextSearch} onChange={handleFreetextChange} />
                </Cell>
                <Cell size="full">
                  <MultiSelect<CvSearchOption>
                    label="Kvalifikationer"
                    getOptionLabel={x => x.optionName || ''}
                    onChange={onQualificationChange}
                    options={qualifications}
                    value={selectedQualifications}
                    variant="outlined"
                    renderOption={(prop: object, option: CvSearchOption) => (
                      <li {...prop} key={option.optionId}>
                        {option.optionName}
                      </li>
                    )}
                  />
                </Cell>
                <Cell size="half">
                  <MultiSelect<CvSearchOption>
                    label="Sprog"
                    getOptionLabel={x => x.optionName || ''}
                    onChange={onLanguageChange}
                    options={languages}
                    value={selectedLanguages}
                    variant="outlined"
                    renderOption={(prop: object, option: CvSearchOption) => (
                      <li {...prop} key={option.optionId}>
                        {option.optionName}
                      </li>
                    )}
                  />
                </Cell>
                <Cell size="half">
                  <MultiSelect<DriverLicensesType>
                    label="Kørekort"
                    getOptionLabel={(option: DriverLicensesType) => getDriverLicencesType(option) ?? ''}
                    onChange={onDriversLicenseChange}
                    options={driverLicencesTypes}
                    value={selectedDriversLicenses}
                    variant="outlined"
                    renderOption={(optionProps: React.HTMLAttributes<HTMLLIElement>, x: DriverLicensesType) => (
                      <li {...optionProps} key={x}>
                        {getDriverLicencesType(x) ?? ''}
                      </li>
                    )}
                  />
                </Cell>
              </Row>
            </AccordionDetails>
          </Accordion>
        </Cell>
        <Cell size="full">
          <Accordion>
            <AccordionSummary>
              <LabelWithHelpIcon
                label="Geografisk placering"
                helpTexts={["Søgningen afgrænses til CV'er på borgere, der bor i og indenfor et angivet antal kilometer fra et postnummer."]}
              />
            </AccordionSummary>
            <AccordionDetails>
              <Row>
                <Cell size="full">
                  <SingleSelect<CvSearchCity>
                    label="Postnummer/By"
                    getOptionLabel={x => `${x.zipCode} ${x.name}` || ''}
                    onChange={onCityChange}
                    options={cities}
                    value={selectedCity}
                    variant="outlined"
                    renderOption={(prop: object, option: CvSearchCity) => (
                      <li {...prop} key={option.zipCode}>
                        {`${option.zipCode} ${option.name}`}
                      </li>
                    )}
                  />
                </Cell>
                <Cell size="full">
                  <Box sx={{ padding: '0 20px 0 20px' }}>
                    <Slider
                      value={numberOfKm}
                      min={5}
                      max={100}
                      marks={[
                        { value: 5, label: '5 km' },
                        { value: numberOfKm || 0, label: `${numberOfKm} km` },
                        { value: 100, label: '100 km' },
                      ]}
                      step={5}
                      valueLabelDisplay="off"
                      valueLabelFormat={value => `${value} km`}
                      onChange={(_, value) => onNumberOfKmChange(value as number)}
                      disabled={!selectedCity}
                    />
                  </Box>
                </Cell>
              </Row>
            </AccordionDetails>
          </Accordion>
        </Cell>
        <Cell size="full">
          <FormControl
            fullWidth
            component="fieldset"
            variant="standard"
            sx={{ backgroundColor: 'white', padding: 1, border: `solid 1px ${ColorPalette.DisabledInputBackground}`, borderRadius: '5px' }}
          >
            <FormGroup>
              <FormControlLabel
                control={<Checkbox size="small" name="showOnlyJobReady" onChange={handleCheckboxChange} checked={showOnlyJobReady} />}
                label="Vis kun jobparate borgere"
              />
              <FormControlLabel
                control={<Checkbox size="small" name="showOnlyFlexjobVisitated" onChange={handleCheckboxChange} checked={showOnlyFlexjobVisitated} />}
                label="Vis kun fleksjobvisiterede"
              />
              <FormControlLabel
                control={<Checkbox size="small" name="showOnlyJobcenter" onChange={handleCheckboxChange} checked={showOnlyJobcenter} />}
                label="Fjern borgere, der ikke er tilknyttet min kommune"
              />
            </FormGroup>
          </FormControl>
        </Cell>
        <Cell size="full">
          <Box display="flex" justifyContent="end" gap={1}>
            <Button loading={isSearching} variant="contained" color="primary" onClick={onSearchClick}>
              Søg
            </Button>
            <Button variant="contained" color="secondary" onClick={onResetClick}>
              Nulstil
            </Button>
          </Box>
        </Cell>
      </Row>
    </Container>
  );
}
